import { useDraggable, useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { columnSortableId } from './kanban-utils';
import type { KanbanColumn, KanbanItem } from './kanban-types';

export interface KanbanColumnViewProps<T extends KanbanItem> {
    column: KanbanColumn;
    items: T[];
    renderCard: (item: T) => React.ReactNode;
    activeItem: T | null;
    canDrop?: (item: T, toColumnId: string) => boolean;
    onLoadMore?: (columnId: string) => void;
    isLoadingMore?: boolean;
    disabled?: boolean;
    renderColumnHeader?: (column: KanbanColumn) => React.ReactNode;
    renderColumnFooter?: (column: KanbanColumn) => React.ReactNode;
    renderEmptyColumn?: (column: KanbanColumn) => React.ReactNode;
    reorderable: boolean;
}

export function KanbanColumnView<T extends KanbanItem>({
    column,
    items,
    renderCard,
    activeItem,
    canDrop,
    onLoadMore,
    isLoadingMore,
    disabled,
    renderColumnHeader,
    renderColumnFooter,
    renderEmptyColumn,
    reorderable,
}: KanbanColumnViewProps<T>) {
    const { t } = useTranslation();
    const { setNodeRef, isOver } = useDroppable({ id: column.id });
    const {
        setNodeRef: setSortableNodeRef,
        transform,
        transition,
        isDragging: isColumnDragging,
        attributes: sortableAttributes,
        listeners: sortableListeners,
    } = useSortable({
        id: columnSortableId(column.id),
        disabled: !reorderable,
        data: { type: 'column' },
    });
    const dropAllowed =
        !activeItem ||
        activeItem.columnId === column.id ||
        !canDrop ||
        canDrop(activeItem, column.id);

    return (
        <div
            ref={setSortableNodeRef}
            style={{
                transform: CSS.Translate.toString(transform),
                transition,
            }}
            data-testid={`kanban-column-${column.id}`}
            data-kanban-column={column.id}
            data-reorderable={reorderable ? 'true' : 'false'}
            className={cn(
                // A recessed lane, not a bordered box: the cards are the surface, so the
                // column steps back a layer instead of drawing a second frame around them.
                'flex max-h-full w-[296px] shrink-0 flex-col rounded-xl bg-bg-inset transition-shadow',
                isColumnDragging && 'opacity-60',
                isOver && dropAllowed && 'ring-2 ring-primary-300',
                isOver && !dropAllowed && 'cursor-not-allowed ring-2 ring-error-300',
            )}
        >
            <div
                className={cn(
                    'sticky top-0 z-10 rounded-t-xl bg-bg-inset px-3 pt-3 pb-2.5',
                    reorderable && 'cursor-grab touch-none',
                )}
                {...(reorderable ? { ...sortableAttributes, ...sortableListeners } : {})}
            >
                {renderColumnHeader ? (
                    renderColumnHeader(column)
                ) : (
                    <div className="flex items-center gap-2">
                        {column.color ? (
                            <span
                                className="size-2.5 rounded-full"
                                style={{ backgroundColor: column.color }}
                            />
                        ) : null}
                        <span className="text-sm font-medium">{column.label}</span>
                        <Badge variant="outline" className="ms-auto text-xs">
                            {column.total}
                        </Badge>
                    </div>
                )}
            </div>
            <div
                ref={setNodeRef}
                data-slot="kanban-column-body"
                className="scrollbar-thin flex min-h-24 flex-1 flex-col gap-2 overflow-y-auto px-2 pb-2"
            >
                {isOver && dropAllowed ? (
                    <div
                        data-testid="kanban-drop-indicator"
                        className="h-0.5 shrink-0 rounded bg-primary-500"
                    />
                ) : null}
                {items.length === 0 && renderEmptyColumn ? renderEmptyColumn(column) : null}
                {items.map((item) => (
                    <KanbanCardShell key={item.id} item={item} disabled={disabled}>
                        {renderCard(item)}
                    </KanbanCardShell>
                ))}
                {onLoadMore && items.length < column.total ? (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground"
                        disabled={isLoadingMore}
                        onClick={() => onLoadMore(column.id)}
                    >
                        {isLoadingMore ? (
                            <Spinner className="size-4" />
                        ) : (
                            t('kanban.loadMore', 'Load more')
                        )}
                    </Button>
                ) : null}
            </div>
            {renderColumnFooter ? (
                <div className="px-2 pb-2">{renderColumnFooter(column)}</div>
            ) : null}
        </div>
    );
}

interface KanbanCardShellProps<T extends KanbanItem> {
    item: T;
    children: React.ReactNode;
    disabled?: boolean;
}

function KanbanCardShell<T extends KanbanItem>({
    item,
    children,
    disabled,
}: KanbanCardShellProps<T>) {
    const { setNodeRef, attributes, listeners, isDragging } = useDraggable({
        id: item.id,
        disabled,
        data: { type: 'card' },
    });

    return (
        <div
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            className={cn('cursor-grab touch-none', isDragging && 'opacity-40')}
        >
            {children}
        </div>
    );
}
